import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as partnerService from '../../../../data-layer/partner/service';
import { PartnerType, SignupExternalStateEnum } from '../../../../data-layer/partner/types';
import { PartnerRegistrationSteps, STEP_MAPPING } from '../types';
import { getNextStep } from '../utils/getNextStep';

export const useRegisterStateMachine = () => {
    const [partnerType, setPartnerType] = useState(PartnerType.SE);
    const [query, setQuery] = useSearchParams();
    const queryStep = query.get('step') as PartnerRegistrationSteps;
    const [step, setStep] = useState(queryStep || PartnerRegistrationSteps.Register);
    const [isInitialized, setIsInitialized] = useState(false);

    useEffect(() => {
        const initializeState = async () => {
            if (isInitialized || queryStep) {
                if (queryStep) {
                    setStep(queryStep);
                }
                setIsInitialized(true);

                return;
            }

            try {
                const stateResponse = await partnerService.getSignupExternalState();
                let nextStep = stateResponse.data.state;

                if (stateResponse.data.state === SignupExternalStateEnum.SELF_EMPLOYED) {
                    const { data } = await partnerService.postSignupExternalSelfEmployed();
                    if (data?.next_state) {
                        nextStep = data.next_state;
                    }
                }

                const backendStep = STEP_MAPPING[nextStep as keyof typeof STEP_MAPPING];

                if (backendStep && backendStep !== PartnerRegistrationSteps.Register) {
                    setStep(backendStep);
                    setQuery({ step: backendStep });
                }
            } finally {
                setIsInitialized(true);
            }
        };

        initializeState();
    }, [isInitialized, queryStep, setQuery]);

    const onNextStep = useCallback(() => {
        const newStep = getNextStep(partnerType, step);
        setStep(newStep);
        setQuery({ step: newStep });
    }, [partnerType, setQuery, step]);

    const onPartnerTypeChange = useCallback((type: PartnerType) => {
        setPartnerType(type);
    }, []);

    const onStepChange = useCallback(
        (newStep: PartnerRegistrationSteps) => {
            setStep(newStep);
            setQuery({ step: newStep });
        },
        [setQuery],
    );

    return { partnerType, step, onNextStep, onPartnerTypeChange, onStepChange, isInitialized };
};
